import { chests as fetchChests, inventory as fetchInventory, type ChestSummary, type Inventory } from '@/net/api';

import { createStore } from './store';

/**
 * The player's unopened chests (the top bar's pill), as Blockari's
 * `state/chests.ts`. Opening one goes through `openChest`, which the UI
 * registers (the server applies the roll, then the reveal plays it).
 */
interface ChestState {
  unopened: ChestSummary[];
  /** A guest's chests that were opened and wait for an account. */
  held: ChestSummary[];
  /** What the player holds from chests (the 2× boost, the equipped frame and title); null until read. */
  inventory: Inventory | null;
}

export const chestStore = createStore<ChestState>({ unopened: [], held: [], inventory: null });

/** The inventory as the server last said it (a chest opened, an equip). */
export function setInventory(inventory: Inventory | null): void {
  chestStore.setState({ inventory });
}

/** Re-read just the inventory (a game spent a boost game; a chest was opened; an equip). */
export async function refreshInventory(): Promise<void> {
  const inv = await fetchInventory();
  if (inv.ok) setInventory(inv.value);
}

/** Fetch the list again: the unopened ones (oldest first) and, for a guest, the held ones. */
export async function refreshChests(): Promise<void> {
  const [r, inv] = await Promise.all([fetchChests(), fetchInventory()]);
  if (inv.ok) setInventory(inv.value);
  if (!r.ok) return;
  const brief = ({ id, source, sourceRef, startRarity, createdAt, opened, held, expiresAt }: ChestSummary): ChestSummary => ({
    id,
    source,
    ...(sourceRef === undefined ? {} : { sourceRef }),
    startRarity,
    createdAt,
    opened,
    held: held === true,
    expiresAt: expiresAt ?? null,
  });
  const live = r.value.chests.filter((c) => !c.opened).map(brief);
  const byAge = (a: ChestSummary, b: ChestSummary): number => a.createdAt.localeCompare(b.createdAt);
  chestStore.setState({
    unopened: live.filter((c) => c.held !== true).sort(byAge),
    held: live.filter((c) => c.held === true).sort(byAge),
  });
}

export function clearChests(): void {
  chestStore.setState({ unopened: [], held: [], inventory: null });
}

/** A guest's open only HOLDS a chest (nothing applied): it leaves the queue and counts as held. */
export function noteHeld(chest: ChestSummary): void {
  const s = chestStore.getState();
  chestStore.setState({
    unopened: s.unopened.filter((c) => c.id !== chest.id),
    held: s.held.some((c) => c.id === chest.id) ? s.held : [...s.held, { ...chest, held: true }],
  });
}

/**
 * The server answered `POST /open`: bookkeeping that must not wait for a list re-read. A guest's open only HOLDS the
 * chest (nothing applied), so it counts as held; and the opened one is dropped from `unopened` locally, so a re-read
 * that fails (offline blip) cannot leave it there for `openNextChest` to re-POST and replay.
 */
export function settleOpened(opened: { chest: ChestSummary; held?: boolean | undefined; inventory: Inventory }): void {
  if (opened.held) noteHeld(opened.chest);
  else chestStore.setState({ unopened: chestStore.getState().unopened.filter((c) => c.id !== opened.chest.id) });
  setInventory(opened.inventory);
}

let opener: ((id: string) => void) | null = null;

/** The UI registers how a chest is opened (main.ts: the server's open, then the reveal). */
export function registerChestOpener(fn: ((id: string) => void) | null): void {
  opener = fn;
}

/** Open this chest (the rank-up's fallback, the pill, the inbox). */
export function openChest(id: string): void {
  opener?.(id);
}

/** Open the oldest unopened chest, if any. Returns whether there was one. */
export function openNextChest(): boolean {
  const next = chestStore.getState().unopened[0];
  if (!next) return false;
  openChest(next.id);
  return true;
}
